/**
 * What an open session does when the person's memberships change under it.
 *
 * Two sources describe the memberships, and they move at different times:
 * `users/{uid}.orgIds` (the mirror `syncOrgMemberClaims` writes, streamed
 * live by `OrgService`) and the ID token's `orgRoles` claim (what the app,
 * the guards and the Firestore rules read), which only changes when the
 * token is refreshed — on its own, up to an hour later. When the two
 * disagree, the token is refreshed; once they agree, the orgs the session
 * had and no longer has decide what happens next.
 *
 * Pure, so the rules are pinned by a table; `MembershipSessionService`
 * carries them out.
 */

/** The two descriptions of the person's memberships, as org ids. */
export interface MembershipView {
  /** `users/{uid}.orgIds`: the server's live mirror of the member docs. */
  readonly orgIds: readonly string[];
  /** The orgs in the ID token's `orgRoles` claim. */
  readonly orgRoles: readonly string[];
}

export type MembershipChange =
  /** Nothing to do. */
  | { readonly kind: 'none' }
  /** The token disagrees with the mirror: force a fresh one. */
  | { readonly kind: 'refresh' }
  /** The last org went: sign out, and say which. */
  | { readonly kind: 'signed-out'; readonly removed: readonly string[] }
  /** An org went and others remain (or staff): carry on, and say which. */
  | { readonly kind: 'switched'; readonly removed: readonly string[] };

/**
 * `prev` is the session's baseline: the first view it saw, then every view
 * in which the mirror and the token agreed. Everything in it — mirror OR
 * token — counts as an org the session had, so a token that still carried
 * an org at sign-in counts as having had it, and an org the mirror showed
 * first (an invite, an approved join request) is not mistaken for a loss.
 *
 * A person who never had an org (a new self-serve signup, a join-request
 * waiter) loses nothing, so is never signed out. An internal admin is never
 * signed out either: they can act as any org without a membership.
 */
export function membershipChange(
  prev: MembershipView,
  next: MembershipView,
  opts: { readonly internalAdmin: boolean },
): MembershipChange {
  if (!inSync(next)) return { kind: 'refresh' };
  const removed = removedOrgs(prev, next.orgIds);
  if (removed.length === 0) return { kind: 'none' };
  if (next.orgIds.length === 0 && !opts.internalAdmin) return { kind: 'signed-out', removed };
  return { kind: 'switched', removed };
}

/** Do the mirror and the token name the same orgs (in any order)? */
export function inSync(view: MembershipView): boolean {
  return sameSet(view.orgIds, view.orgRoles);
}

/** Orgs the baseline had (mirror or token) that the mirror no longer lists. */
export function removedOrgs(prev: MembershipView, orgIds: readonly string[]): string[] {
  const now = new Set(orgIds);
  const had = new Set([...prev.orgIds, ...prev.orgRoles]);
  return [...had].filter((id) => !now.has(id));
}

/**
 * Did a forced token refresh fail because the session is over (refresh
 * tokens revoked, account disabled or deleted)? The SDK signs such a
 * session out itself. A network failure is not this: the next change
 * tries again.
 */
export function isSessionRevoked(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && SESSION_ENDED_CODES.has(code);
}

const SESSION_ENDED_CODES = new Set([
  // securetoken TOKEN_EXPIRED (revoked) and USER_NOT_FOUND (deleted)
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/invalid-user-token',
  'auth/user-not-found',
  'auth/invalid-refresh-token',
]);

/** "Your access to Acme was removed." — "an organisation" when no name is known. */
export function accessRemovedMessage(names: readonly string[]): string {
  const known = names.filter((n) => n.trim().length > 0);
  if (known.length === 0) return 'Your access to an organisation was removed.';
  const list =
    known.length === 1
      ? known[0]
      : `${known.slice(0, -1).join(', ')} and ${known[known.length - 1]}`;
  return `Your access to ${list} was removed.`;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((id) => right.has(id));
}
